import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  bringToFront,
  canEdit,
  deleteSelection,
  duplicateSelection,
  expandSelection,
  formatInZone,
  groupNodes,
  LOCK_HEARTBEAT_SECONDS,
  moveBackward,
  moveForward,
  sendToBack,
  ungroupNodes,
  parseCsv,
  mapUserRows,
  mapVmRows,
  mapVolumeRows,
  mergeUsers,
  mergeVms,
  mergeVolumes,
  nextVolumeColor,
  vmKey,
  recommendedClusterStorageSize,
  sceneVolumes,
  commentAnchorKey,
  followedViewport,
  presenceColor,
  recommendedHostSize,
  remoteSelectionColors,
  stagePosForCenter,
  viewportCenterWorld,
} from '@system-atlas/shared';
import type {
  AccessLevel,
  CommentAnchor,
  Connector,
  DeviceKind,
  EnvironmentType,
  NodeType,
  PortDef,
  PortKind,
  SceneComment,
  SceneDoc,
  SceneNode,
  VlanDef,
  ImportDiff,
  ImportSource,
  MergeStrategy,
  VmDef,
  VolumeDef,
} from '@system-atlas/shared';
import type Konva from 'konva';
import { ApiError, api, type ImageMeta } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { Alert, MenuItem } from '../components/ui';
import { cn } from '../lib/utils';
import { EditorCanvas } from './EditorCanvas';
import { Minimap } from './Minimap';
import { PropertyPanel } from './PropertyPanel';
import { ImageLibraryPanel } from './ImageLibraryPanel';
import { VlanManager } from './VlanManager';
import { ImportDialog } from './import/ImportDialog';
import { UserDirectoryManager } from './UserDirectoryManager';
import { ShortcutHelpModal } from './ShortcutHelpModal';
import { Toolbar, type EditorTool, type SaveStatus } from './Toolbar';
import { createNode, newId } from './nodeFactory';
import { ENV_COLORS } from './colors';
import { canvasForFormat, clampZoom, effectiveCanvas, type CanvasFormat } from './canvasFormats';
import { exportScenePdf, exportScenePng, exportSceneSvg, type ExportFormat } from './exportScene';
import { useCollabScene } from './collab/useCollabScene';
import { PresenceBar } from './collab/PresenceBar';
import { CommentsOverlay, type CommentDraft } from './comments/CommentsOverlay';
import { InsertSidebar } from './InsertSidebar';

export function SceneEditor({
  drawingId,
  initialScene,
  access,
  collabEnabled,
}: {
  drawingId: string;
  initialScene: SceneDoc;
  access: AccessLevel;
  collabEnabled: boolean;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { scene, setScene, undo, redo, canUndo, canRedo, ready, connection, presence } = useCollabScene({
    drawingId,
    initialScene,
    enabled: collabEnabled,
    localUser: user ? { userId: user.id, name: user.displayName } : null,
  });
  // Auswahl lebt bewusst außerhalb der Undo-History.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Einzeln angewaehlter Switch-Port (ausserhalb der Undo-History, analog selectedIds).
  const [selectedPort, setSelectedPort] = useState<{ nodeId: string; portIndex: number } | null>(null);
  const [tool, setToolState] = useState<EditorTool>('select');
  const [pendingFrom, setPendingFrom] = useState<{ nodeId: string; portIndex?: number } | null>(null);
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [vlansOpen, setVlansOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [userDirOpen, setUserDirOpen] = useState(false);
  const [imagePickNodeId, setImagePickNodeId] = useState<string | null>(null);
  const [spaceDown, setSpaceDown] = useState(false);
  // Waehrend des PNG/PDF-Exports: Auswahl-Optik (Highlight/Transformer/Hilfslinien) ausblenden.
  const [exporting, setExporting] = useState(false);
  // Rechtsklick-Kontextmenü: Position relativ zum Canvas-Container + Ziel-Knoten (oder leer).
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; nodeId: string | null } | null>(null);

  const editable = canEdit(access);
  const [lockState, setLockState] = useState<'idle' | 'locked' | 'blocked' | 'lost'>('idle');
  const [lockHolder, setLockHolder] = useState('');
  // Collab: bearbeitbar erst nach dem ersten Sync; 'denied' (Session weg) -> read-only.
  // Flag aus: unveraendert das alte Sperrmodell.
  const readOnly = collabEnabled
    ? !editable || !ready || connection === 'denied'
    : !editable || lockState !== 'locked';
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;

  // Phase 3 (Remote-Auswahl): eigene Auswahl in den Awareness-State melden.
  // Auch read-only-Benutzer melden (view-only darf Awareness senden, Spec 3.2).
  const reportSelection = presence.setSelection;
  useEffect(() => {
    if (collabEnabled) reportSelection(selectedIds);
  }, [collabEnabled, reportSelection, selectedIds]);

  // Fremde Auswahl -> Farbe (fuer die Hervorhebung im Canvas).
  const remoteSelections = useMemo(
    () => (collabEnabled ? remoteSelectionColors(presence.others) : undefined),
    [collabEnabled, presence.others],
  );

  // Phase 4 (Follow-Modus): wem folge ich gerade? Auswahl lebt nur lokal;
  // der Awareness-State wird per Effekt gespiegelt (Spec Abschnitt 6).
  const [followingUserId, setFollowingUserId] = useState<string | null>(null);
  const reportFollowing = presence.setFollowing;
  useEffect(() => {
    if (collabEnabled) reportFollowing(followingUserId);
  }, [collabEnabled, reportFollowing, followingUserId]);

  // Kommentare (Phase 5): Entwurf (nach Tap im Kommentar-Modus) + offener Thread.
  const [commentDraft, setCommentDraft] = useState<CommentDraft | null>(null);
  const [openThreadKey, setOpenThreadKey] = useState<string | null>(null);

  const dirtyRef = useRef(false);
  const editorRootRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage | null>(null);
  const [viewport, setViewport] = useState({ width: 1000, height: 600 });
  const [scale, setScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 24, y: 24 });
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Eigenen Sichtbereich melden (Weltzentrum + Zoom; Drosselung uebernimmt der Hook).
  const reportViewport = presence.setViewport;
  useEffect(() => {
    if (!collabEnabled) return;
    const center = viewportCenterWorld(stagePos, scale, { width: viewport.width, height: viewport.height });
    reportViewport({ x: center.x, y: center.y, scale });
  }, [collabEnabled, reportViewport, stagePos, scale, viewport.width, viewport.height]);

  // Follow anwenden: eigene Ansicht folgt dem Weltzentrum/Zoom der Zielperson.
  // Verlaesst die Zielperson die Zeichnung, endet das Folgen automatisch.
  useEffect(() => {
    if (!collabEnabled || !followingUserId) return;
    const v = followedViewport(presence.others, followingUserId);
    if (!v) {
      setFollowingUserId(null);
      return;
    }
    const s = clampZoom(v.scale); // fremdem Zoom nicht blind vertrauen
    setScale(s);
    setStagePos(stagePosForCenter({ x: v.x, y: v.y }, s, { width: viewport.width, height: viewport.height }));
  }, [collabEnabled, followingUserId, presence.others, viewport.width, viewport.height]);

  const canvas = useMemo(() => effectiveCanvas(scene), [scene]);

  // PropertyPanel-Breite (Drag-Divider, in localStorage persistiert); mobile Drawer-States.
  const PANEL_WIDTH_KEY = 'system-atlas.editor.panel-width';
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const stored = Number(localStorage.getItem(PANEL_WIDTH_KEY));
    return Number.isFinite(stored) && stored >= 220 && stored <= 560 ? stored : 256;
  });
  const [propsOpenMobile, setPropsOpenMobile] = useState(false);
  const [minimapOpen, setMinimapOpen] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth));
    } catch {
      // localStorage nicht verfügbar – Breite gilt nur für die Sitzung.
    }
  }, [panelWidth]);

  // Drag-Divider: Panelbreite an der linken Kante des PropertyPanels ziehen.
  // Laufender Drag wird beim Unmount sauber beendet (Listener entfernen).
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => resizeCleanupRef.current?.(), []);
  const startResize = (e: ReactPointerEvent) => {
    e.preventDefault();
    const onMove = (ev: globalThis.PointerEvent) => {
      const right = panelRef.current?.getBoundingClientRect().right ?? window.innerWidth;
      setPanelWidth(Math.min(560, Math.max(220, right - ev.clientX)));
    };
    const stop = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', stop);
      resizeCleanupRef.current = null;
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', stop);
    resizeCleanupRef.current = stop;
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () =>
      setViewport({ width: Math.max(el.clientWidth, 320), height: Math.max(el.clientHeight, 240) });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Vollbild-Status spiegeln.
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === editorRootRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const markDirty = () => {
    if (collabEnabled) return; // Persistenz uebernimmt der Server (debounced)
    dirtyRef.current = true;
    setStatus('idle');
  };

  const fitBox = useCallback(
    (box: { x: number; y: number; width: number; height: number }) => {
      setFollowingUserId(null); // eigene Interaktion (Einpassen/Reset) beendet das Folgen
      const pad = 48;
      const sx = viewport.width / (box.width + pad * 2);
      const sy = viewport.height / (box.height + pad * 2);
      const newScale = clampZoom(Math.min(sx, sy));
      setScale(newScale);
      setStagePos({
        x: viewport.width / 2 - (box.x + box.width / 2) * newScale,
        y: viewport.height / 2 - (box.y + box.height / 2) * newScale,
      });
    },
    [viewport],
  );

  const fitToView = useCallback(() => {
    if (scene.nodes.length === 0) {
      fitBox({ x: canvas.x, y: canvas.y, width: canvas.width, height: canvas.height });
      return;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of scene.nodes) {
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + n.width);
      maxY = Math.max(maxY, n.y + n.height);
    }
    fitBox({ x: minX, y: minY, width: maxX - minX, height: maxY - minY });
  }, [scene.nodes, canvas.x, canvas.y, canvas.width, canvas.height, fitBox]);

  // Zoom-to-Selection: auf die Bounding-Box der Auswahl einpassen (Fallback: alles).
  const fitToSelection = useCallback(() => {
    const sel = scene.nodes.filter((n) => selectedIds.includes(n.id));
    if (sel.length === 0) {
      fitToView();
      return;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of sel) {
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + n.width);
      maxY = Math.max(maxY, n.y + n.height);
    }
    fitBox({ x: minX, y: minY, width: maxX - minX, height: maxY - minY });
  }, [scene.nodes, selectedIds, fitBox, fitToView]);

  // Einmalig einpassen, sobald der Viewport vermessen ist.
  const didFitRef = useRef(false);
  useEffect(() => {
    if (didFitRef.current || viewport.width <= 320) return;
    didFitRef.current = true;
    fitBox({ x: canvas.x, y: canvas.y, width: canvas.width, height: canvas.height });
  }, [viewport.width, canvas.x, canvas.y, canvas.width, canvas.height, fitBox]);

  const zoomBy = (factor: number) => {
    setFollowingUserId(null); // eigene Interaktion (Zoom) beendet das Folgen
    const newScale = clampZoom(scale * factor);
    const cx = viewport.width / 2;
    const cy = viewport.height / 2;
    const worldX = (cx - stagePos.x) / scale;
    const worldY = (cy - stagePos.y) / scale;
    setScale(newScale);
    setStagePos({ x: cx - worldX * newScale, y: cy - worldY * newScale });
  };

  const onViewportChange = (nextScale: number, nextPos: { x: number; y: number }) => {
    setFollowingUserId(null); // eigene Interaktion (Canvas-Geste) beendet das Folgen
    setScale(nextScale);
    setStagePos(nextPos);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void editorRootRef.current?.requestFullscreen();
    }
  };

  const handleExport = (format: ExportFormat) => {
    const filename = 'system-atlas-zeichnung';
    if (format === 'svg') {
      void exportSceneSvg(scene, filename, (id) => new URL(api.imageRawUrl(id), window.location.origin).href);
      return;
    }
    const stage = stageRef.current;
    if (!stage) return;
    setExporting(true);
    // Nach dem Re-Render (Transformer/Highlight weg) exportieren, dann zuruecksetzen.
    requestAnimationFrame(() => {
      try {
        if (format === 'png') exportScenePng(stage, scene, filename);
        else exportScenePdf(stage, scene, filename);
      } finally {
        setExporting(false);
      }
    });
  };

  const addNode = (type: NodeType, env?: EnvironmentType) => {
    if (readOnlyRef.current) return;
    const node = createNode({
      type,
      environmentType: env,
      x: 60 + Math.round(Math.random() * 60),
      y: 60 + Math.round(Math.random() * 60),
    });
    setScene((s) => ({ ...s, nodes: [...s.nodes, node] }));
    setSelectedIds([node.id]);
    setToolState('select');
    markDirty();
  };

  const addDevice = (kind: DeviceKind) => {
    if (readOnlyRef.current) return;
    const node = createNode({
      type: 'device',
      deviceKind: kind,
      x: 60 + Math.round(Math.random() * 60),
      y: 60 + Math.round(Math.random() * 60),
    });
    setScene((s) => ({ ...s, nodes: [...s.nodes, node] }));
    setSelectedIds([node.id]);
    setToolState('select');
    markDirty();
  };

  const addSwitch = () => {
    if (readOnlyRef.current) return;
    const node = createNode({
      type: 'switch',
      x: 60 + Math.round(Math.random() * 60),
      y: 60 + Math.round(Math.random() * 60),
    });
    setScene((s) => ({ ...s, nodes: [...s.nodes, node] }));
    setSelectedIds([node.id]);
    setToolState('select');
    markDirty();
  };

  const addHost = () => {
    if (readOnlyRef.current) return;
    const node = createNode({
      type: 'host',
      x: 60 + Math.round(Math.random() * 60),
      y: 60 + Math.round(Math.random() * 60),
    });
    setScene((s) => ({ ...s, nodes: [...s.nodes, node] }));
    setSelectedIds([node.id]);
    setToolState('select');
    markDirty();
  };

  const addClusterStorage = () => {
    if (readOnlyRef.current) return;
    const node = createNode({
      type: 'clusterstorage',
      x: 60 + Math.round(Math.random() * 60),
      y: 60 + Math.round(Math.random() * 60),
    });
    setScene((s) => ({ ...s, nodes: [...s.nodes, node] }));
    setSelectedIds([node.id]);
    setToolState('select');
    markDirty();
  };

  const addSwitchLegend = () => {
    if (readOnlyRef.current) return;
    const firstSwitch = scene.nodes.find((n) => n.type === 'switch');
    const node = createNode({
      type: 'switch-legend',
      x: 60 + Math.round(Math.random() * 60),
      y: 60 + Math.round(Math.random() * 60),
    });
    if (firstSwitch) node.switchLegend = { ...node.switchLegend, sourceNodeId: firstSwitch.id };
    setScene((s) => ({ ...s, nodes: [...s.nodes, node] }));
    setSelectedIds([node.id]);
    setToolState('select');
    markDirty();
  };

  const addFirewall = () => {
    if (readOnlyRef.current) return;
    const node = createNode({
      type: 'firewall',
      x: 60 + Math.round(Math.random() * 60),
      y: 60 + Math.round(Math.random() * 60),
    });
    setScene((s) => ({ ...s, nodes: [...s.nodes, node] }));
    setSelectedIds([node.id]);
    setToolState('select');
    markDirty();
  };

  const addFirewallLegend = () => {
    if (readOnlyRef.current) return;
    const firstFirewall = scene.nodes.find((n) => n.type === 'firewall');
    const node = createNode({
      type: 'firewall-legend',
      x: 60 + Math.round(Math.random() * 60),
      y: 60 + Math.round(Math.random() * 60),
    });
    if (firstFirewall) node.firewallLegend = { ...node.firewallLegend, sourceNodeId: firstFirewall.id };
    setScene((s) => ({ ...s, nodes: [...s.nodes, node] }));
    setSelectedIds([node.id]);
    setToolState('select');
    markDirty();
  };

  // Gesammelter Commit mehrerer Knoten-Patches: genau 1 setScene = 1 Undo-Schritt.
  const updateNodes = useCallback((changes: { id: string; patch: Partial<SceneNode> }[]) => {
    if (readOnlyRef.current || changes.length === 0) return;
    const byId = new Map(changes.map((c) => [c.id, c.patch]));
    setScene((s) => ({
      ...s,
      nodes: s.nodes.map((n) => (byId.has(n.id) ? { ...n, ...byId.get(n.id)! } : n)),
    }));
    markDirty();
  }, []);

  const updateNode = useCallback(
    (id: string, patch: Partial<SceneNode>) => updateNodes([{ id, patch }]),
    [updateNodes],
  );

  // Multi-Edit: dieselbe Eigenschaft (z. B. Farbe) auf alle ausgewählten Knoten anwenden.
  const updateSelectedNodes = useCallback(
    (patch: Partial<SceneNode>) => updateNodes(selectedIds.map((id) => ({ id, patch }))),
    [selectedIds, updateNodes],
  );

  // Aktualisiert genau einen Switch-Port (1 setScene = 1 Undo-Schritt).
  const updatePort = useCallback((nodeId: string, portIndex: number, patch: Partial<PortDef>) => {
    if (readOnlyRef.current) return;
    setScene((s) => ({
      ...s,
      nodes: s.nodes.map((n) => {
        if (n.id !== nodeId || !n.switch) return n;
        const ports = n.switch.ports.map((p) => (p.index === portIndex ? { ...p, ...patch } : p));
        return { ...n, switch: { ...n.switch, ports } };
      }),
    }));
    markDirty();
  }, []);

  const addImageNode = (image: ImageMeta) => {
    if (readOnlyRef.current) return;
    const aspect = image.width && image.height ? image.height / image.width : 1;
    const width = 160;
    const height = Math.max(40, Math.round(width * aspect));
    const node: SceneNode = {
      ...createNode({ type: 'image' }),
      imageId: image.id,
      width,
      height,
      environmentType: image.environmentType,
      stroke: ENV_COLORS[image.environmentType].stroke,
    };
    setScene((s) => ({ ...s, nodes: [...s.nodes, node] }));
    setSelectedIds([node.id]);
    // Panel bleibt offen, damit mehrere Bilder nacheinander eingefügt werden können.
    markDirty();
  };

  const pickDeviceImage = (image: ImageMeta) => {
    const id = imagePickNodeId;
    if (id === null) return;
    const target = scene.nodes.find((n) => n.id === id);
    if (target?.device) updateNode(id, { device: { ...target.device, customImageId: image.id } });
    setImagePickNodeId(null);
  };

  const updateConnector = (id: string, patch: Partial<Connector>) => {
    if (readOnlyRef.current) return;
    setScene((s) => ({
      ...s,
      connectors: s.connectors.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    }));
    markDirty();
  };

  const portTypeOf = (e: { nodeId: string; portIndex?: number }): PortKind | undefined => {
    if (e.portIndex === undefined) return undefined;
    const node = scene.nodes.find((n) => n.id === e.nodeId);
    return node?.switch?.ports.find((p) => p.index === e.portIndex)?.portType;
  };

  // Verbindet zwei Endpunkte (Knoten oder Switch-Port). Kabeltyp automatisch (SFP->Glas);
  // steckt ein Geraet an einem Port ein, wird PortDef.deviceId gesetzt (Single Source of Truth).
  const plugConnector = (
    from: { nodeId: string; portIndex?: number },
    to: { nodeId: string; portIndex?: number },
  ) => {
    if (readOnlyRef.current) return;
    const fiber = portTypeOf(from) === 'sfp' || portTypeOf(to) === 'sfp';
    const connector: Connector = {
      id: newId(),
      from,
      to,
      cableType: fiber ? 'fiber' : 'copper',
      ...(fiber ? { fiberMode: 'mm' as const } : {}),
    };
    const deviceEnd = (port: { nodeId: string; portIndex?: number }, other: { nodeId: string; portIndex?: number }) =>
      port.portIndex !== undefined && scene.nodes.find((n) => n.id === other.nodeId)?.device ? other.nodeId : undefined;
    const fromDevice = deviceEnd(from, to);
    const toDevice = deviceEnd(to, from);
    setScene((s) => ({
      ...s,
      connectors: [...s.connectors, connector],
      nodes: s.nodes.map((n) => {
        if (from.portIndex !== undefined && n.id === from.nodeId && n.switch && fromDevice) {
          return {
            ...n,
            switch: {
              ...n.switch,
              ports: n.switch.ports.map((p) => (p.index === from.portIndex ? { ...p, deviceId: fromDevice } : p)),
            },
          };
        }
        if (to.portIndex !== undefined && n.id === to.nodeId && n.switch && toDevice) {
          return {
            ...n,
            switch: {
              ...n.switch,
              ports: n.switch.ports.map((p) => (p.index === to.portIndex ? { ...p, deviceId: toDevice } : p)),
            },
          };
        }
        return n;
      }),
    }));
    markDirty();
  };

  const handleConnectClick = (endpoint: { nodeId: string; portIndex?: number }) => {
    if (!pendingFrom) {
      setPendingFrom(endpoint);
    } else if (pendingFrom.nodeId !== endpoint.nodeId || pendingFrom.portIndex !== endpoint.portIndex) {
      plugConnector(pendingFrom, endpoint);
      setPendingFrom(null);
    }
  };

  const addVlan = (vlan: VlanDef) => {
    if (readOnlyRef.current) return;
    setScene((s) => ({ ...s, vlans: [...(s.vlans ?? []), vlan] }));
    markDirty();
  };

  const updateVlan = (id: string, patch: Partial<VlanDef>) => {
    if (readOnlyRef.current) return;
    setScene((s) => ({
      ...s,
      vlans: (s.vlans ?? []).map((v) => (v.id === id ? { ...v, ...patch } : v)),
    }));
    markDirty();
  };

  const removeVlan = (id: string) => {
    if (readOnlyRef.current) return;
    setScene((s) => ({ ...s, vlans: (s.vlans ?? []).filter((v) => v.id !== id) }));
    markDirty();
  };

  // Plant einen CSV-Import: parst, mappt, mergt und liefert Diff + apply-Funktion (oder einen Fehler).
  const planImport = (
    source: ImportSource,
    text: string,
    strategy: MergeStrategy,
  ): { diff: ImportDiff; apply: () => void } | { error: string } => {
    let parsed;
    try {
      parsed = parseCsv(text);
    } catch {
      return { error: t('editor.import.parseError') };
    }
    if (parsed.headers.length === 0) return { error: t('editor.import.emptyError') };

    if (source === 'azure-users') {
      const imported = mapUserRows(parsed.rows);
      if (imported.length === 0) return { error: t('editor.import.noUsers') };
      const { users, diff } = mergeUsers(scene.users ?? [], imported, strategy);
      return {
        diff,
        apply: () => {
          setScene((s) => ({ ...s, users }));
          markDirty();
        },
      };
    }

    if (source === 'hyperv-storage') {
      const mappedVols = mapVolumeRows(parsed.rows);
      if (mappedVols.length === 0) return { error: t('editor.import.noVolumes') };
      const byCluster = new Map<string, VolumeDef[]>();
      const exportedAtByCluster = new Map<string, string | undefined>();
      for (const { cluster, exportedAt, volume } of mappedVols) {
        const arr = byCluster.get(cluster) ?? [];
        arr.push(volume);
        byCluster.set(cluster, arr);
        if (!exportedAtByCluster.has(cluster)) exportedAtByCluster.set(cluster, exportedAt);
      }
      let recognized = 0;
      let added = 0;
      let updated = 0;
      let removed = 0;
      const plan: { nodeId?: string; cluster: string; volumes: VolumeDef[]; importedAt?: string; adopt?: boolean }[] = [];
      for (const [cluster, importedVols] of byCluster) {
        const importedNames = new Set(importedVols.map((v) => v.name.trim().toLowerCase()));
        let node = scene.nodes.find(
          (n) => n.type === 'clusterstorage' && n.clusterStorage && n.label.trim().toLowerCase() === cluster.trim().toLowerCase(),
        );
        let adopt = false; // reused unlabeled node -> set its label/cluster on apply
        if (!node) {
          node = scene.nodes.find(
            (n) => n.type === 'clusterstorage' && n.clusterStorage && n.label.trim() === '' &&
              n.clusterStorage.volumes.some((v) => importedNames.has(v.name.trim().toLowerCase())),
          );
          if (node) adopt = true;
        }
        const { volumes, diff } = mergeVolumes(node?.clusterStorage?.volumes ?? [], importedVols, strategy);
        recognized += diff.recognized;
        added += diff.added;
        updated += diff.updated;
        removed += diff.removed;
        plan.push({ nodeId: node?.id, cluster, volumes, importedAt: exportedAtByCluster.get(cluster), adopt });
      }
      const apply = () => {
        setScene((s) => {
          let nodes = s.nodes;
          for (const p of plan) {
            const size = recommendedClusterStorageSize(p.volumes.length);
            if (p.nodeId) {
              nodes = nodes.map((n) =>
                n.id === p.nodeId && n.clusterStorage
                  ? { ...n, ...(p.adopt ? { label: p.cluster } : {}), clusterStorage: { ...n.clusterStorage, ...(p.adopt ? { cluster: p.cluster } : {}), volumes: p.volumes, ...(p.importedAt ? { importedAt: p.importedAt } : {}) }, width: size.width, height: size.height }
                  : n,
              );
            } else {
              const node = createNode({ type: 'clusterstorage', x: 60 + Math.round(Math.random() * 60), y: 60 + Math.round(Math.random() * 60) });
              nodes = [...nodes, { ...node, label: p.cluster, clusterStorage: { ...node.clusterStorage!, cluster: p.cluster, volumes: p.volumes, ...(p.importedAt ? { importedAt: p.importedAt } : {}) }, width: size.width, height: size.height }];
            }
          }
          return { ...s, nodes };
        });
        markDirty();
      };
      return { diff: { recognized, added, updated, removed }, apply };
    }

    const mapped = mapVmRows(parsed.rows);
    if (mapped.length === 0) return { error: t('editor.import.noVms') };
    const byHost = new Map<string, VmDef[]>();
    const capByHost = new Map<string, { cores?: number; ram?: number; ip?: string }>();
    // VM-Identitaet (Host + vmId/Name, wie shared vmKey) -> gewuenschter Volume-Name.
    // Wird beim Apply genutzt, um vm.volumeRef aus dem aktuellen Storage-Bestand aufzuloesen.
    const volNameByHostVm = new Map<string, string>();
    const wantedVolumes = new Set<string>();
    for (const { host, hostCores, hostRam, hostIp, volumeName, vm } of mapped) {
      const arr = byHost.get(host) ?? [];
      arr.push(vm);
      byHost.set(host, arr);
      if (!capByHost.has(host)) capByHost.set(host, { cores: hostCores, ram: hostRam, ip: hostIp });
      if (volumeName) {
        wantedVolumes.add(volumeName);
        volNameByHostVm.set(`${host} ${vmKey(vm)}`, volumeName);
      }
    }
    let recognized = 0;
    let added = 0;
    let updated = 0;
    let removed = 0;
    const plan: { nodeId?: string; hostName: string; vms: VmDef[]; cap: { cores?: number; ram?: number; ip?: string }; existingKeys: Set<string> }[] = [];
    for (const [hostName, importedVms] of byHost) {
      const node = scene.nodes.find(
        (n) => n.type === 'host' && n.host && n.label.trim().toLowerCase() === hostName.trim().toLowerCase(),
      );
      const { vms, diff } = mergeVms(node?.host?.vms ?? [], importedVms, strategy);
      recognized += diff.recognized;
      added += diff.added;
      updated += diff.updated;
      removed += diff.removed;
      plan.push({ nodeId: node?.id, hostName, vms, cap: capByHost.get(hostName) ?? {}, existingKeys: new Set((node?.host?.vms ?? []).map(vmKey)) });
    }
    const apply = () => {
      setScene((s) => {
        let nodes = s.nodes;
        // 1) VM->Volume: nur wenn die CSV ueberhaupt Volume-Namen enthaelt. Fehlende Volumes
        //    (nach Name, ueber ALLE clusterstorage-Knoten) anlegen und Name->id-Map aufbauen.
        //    Laeuft im setScene, damit gerade angelegte Volumes sofort referenzierbar sind.
        const nameToId = new Map<string, string>();
        if (wantedVolumes.size > 0) {
          for (const vr of sceneVolumes(nodes)) nameToId.set(vr.volume.name.trim().toLowerCase(), vr.volume.id);
          const missing = [...wantedVolumes].filter((n) => !nameToId.has(n.trim().toLowerCase()));
          if (missing.length > 0) {
            // Ziel: erstes vorhandenes clusterstorage, sonst ein neues anlegen.
            let target = nodes.find((n) => n.type === 'clusterstorage' && n.clusterStorage);
            if (!target) {
              const node = createNode({ type: 'clusterstorage', x: 60 + Math.round(Math.random() * 60), y: 60 + Math.round(Math.random() * 60) });
              target = node;
              nodes = [...nodes, node];
            }
            const targetId = target.id;
            nodes = nodes.map((n) => {
              if (n.id !== targetId || !n.clusterStorage) return n;
              const vols = [...n.clusterStorage.volumes];
              for (const name of missing) {
                const key = name.trim().toLowerCase();
                if (nameToId.has(key)) continue; // Case-Duplikat innerhalb der CSV
                const vol: VolumeDef = { id: newId(), name, color: nextVolumeColor(vols) };
                vols.push(vol);
                nameToId.set(key, vol.id);
              }
              const size = recommendedClusterStorageSize(vols.length);
              return { ...n, clusterStorage: { ...n.clusterStorage, volumes: vols }, width: size.width, height: size.height };
            });
          }
        }
        // 2) Host-Schleife wie bisher; jede VM bekommt volumeRef aus nameToId (Name-Match).
        //    Ohne gewuenschte Volumes bleibt p.vms unveraendert (Back-Compat).
        for (const p of plan) {
          const vms = wantedVolumes.size === 0
            ? p.vms
            : p.vms.map((vm) => {
                if (strategy === 'add-new' && p.existingKeys.has(vmKey(vm))) return vm; // add-new: bestehende VMs unveraendert
                const volumeName = volNameByHostVm.get(`${p.hostName} ${vmKey(vm)}`);
                if (!volumeName) return vm; // keine Zuordnung -> bestehende volumeRef bewahren
                const id = nameToId.get(volumeName.trim().toLowerCase());
                return id ? { ...vm, volumeRef: id } : vm;
              });
          const hostPatch = {
            vms,
            ...(p.cap.cores !== undefined ? { cores: p.cap.cores } : {}),
            ...(p.cap.ram !== undefined ? { ram: p.cap.ram } : {}),
            ...(p.cap.ip !== undefined ? { ip: p.cap.ip } : {}),
          };
          if (p.nodeId) {
            nodes = nodes.map((n) => {
              if (n.id !== p.nodeId || !n.host) return n;
              const size = recommendedHostSize(vms.length);
              return { ...n, host: { ...n.host, ...hostPatch }, width: size.width, height: size.height };
            });
          } else {
            const node = createNode({ type: 'host', x: 60 + Math.round(Math.random() * 60), y: 60 + Math.round(Math.random() * 60) });
            const size = recommendedHostSize(vms.length);
            nodes = [...nodes, { ...node, label: p.hostName, host: { ...node.host!, ...hostPatch }, width: size.width, height: size.height }];
          }
        }
        return { ...s, nodes };
      });
      markDirty();
    };
    return { diff: { recognized, added, updated, removed }, apply };
  };

  const importPreview = (source: ImportSource, text: string, strategy: MergeStrategy) => {
    const r = planImport(source, text, strategy);
    return 'error' in r ? { error: r.error } : { diff: r.diff };
  };
  const importApply = (source: ImportSource, text: string, strategy: MergeStrategy): { error?: string } => {
    if (readOnlyRef.current) return {};
    const r = planImport(source, text, strategy);
    if ('error' in r) return { error: r.error };
    r.apply();
    return {};
  };

  const removeUser = (id: string) => {
    if (readOnlyRef.current) return;
    setScene((s) => ({ ...s, users: (s.users ?? []).filter((u) => u.id !== id) }));
    markDirty();
  };
  const clearUsers = () => {
    if (readOnlyRef.current) return;
    setScene((s) => ({ ...s, users: [] }));
    markDirty();
  };

  const setCanvasFormat = (format: CanvasFormat) => {
    if (readOnlyRef.current) return;
    setScene((s) => ({ ...s, canvas: canvasForFormat(format) }));
    markDirty();
  };

  // Globaler Linien-Brücken-Schalter (Baustein D): undefined = an. Aus = false.
  const toggleLineBridges = () => {
    if (readOnlyRef.current) return;
    setScene((s) => ({ ...s, lineBridges: s.lineBridges === false ? true : false }));
    markDirty();
  };

  const removeSelected = () => {
    if (readOnlyRef.current || selectedIds.length === 0) return;
    // Wird ein eingestecktes Kabel geloescht, die deviceId der betroffenen Ports wieder raeumen.
    const clearPorts = scene.connectors
      .filter((c) => selectedIds.includes(c.id))
      .flatMap((c) =>
        [c.from, c.to].filter(
          (e): e is { nodeId: string; portIndex: number } => 'nodeId' in e && e.portIndex !== undefined,
        ),
      );
    setScene((s) => {
      const cleared = clearPorts.length
        ? s.nodes.map((n) => {
            const idx = new Set(clearPorts.filter((e) => e.nodeId === n.id).map((e) => e.portIndex));
            if (idx.size === 0 || !n.switch) return n;
            return {
              ...n,
              switch: { ...n.switch, ports: n.switch.ports.map((p) => (idx.has(p.index) ? { ...p, deviceId: undefined } : p)) },
            };
          })
        : s.nodes;
      return deleteSelection({ ...s, nodes: cleared }, selectedIds);
    });
    setSelectedIds([]);
    markDirty();
  };

  const duplicateSelected = () => {
    if (readOnlyRef.current || selectedIds.length === 0) return;
    // Außerhalb des Updaters berechnen: idGen ist nicht pur (StrictMode).
    const { scene: next, newIds } = duplicateSelection(scene, selectedIds, newId);
    if (newIds.length === 0) return;
    setScene(next);
    setSelectedIds(newIds);
    markDirty();
  };

  const groupSelection = () => {
    if (readOnlyRef.current || selectedIds.length < 2) return;
    // groupId außerhalb des Updaters erzeugen (idGen ist nicht pur).
    const next = groupNodes(scene.nodes, selectedIds, newId);
    setScene((s) => ({ ...s, nodes: next }));
    markDirty();
  };

  const ungroupSelection = () => {
    if (readOnlyRef.current || selectedIds.length === 0) return;
    setScene((s) => ({ ...s, nodes: ungroupNodes(s.nodes, selectedIds) }));
    markDirty();
  };

  // Ebenen: Auswahl im jeweiligen Tier verschieben (1 setScene = 1 Undo-Schritt).
  const reorderSelection = (action: 'front' | 'forward' | 'backward' | 'back') => {
    if (readOnlyRef.current || selectedIds.length === 0) return;
    const fn =
      action === 'front' ? bringToFront : action === 'forward' ? moveForward : action === 'backward' ? moveBackward : sendToBack;
    setScene((s) => ({ ...s, nodes: fn(s.nodes, selectedIds) }));
    markDirty();
  };

  const doUndo = () => {
    if (readOnlyRef.current) return;
    undo();
    markDirty();
  };

  const doRedo = () => {
    if (readOnlyRef.current) return;
    redo();
    markDirty();
  };

  const setTool = (next: EditorTool) => {
    setToolState(next);
    setPendingFrom(null);
  };

  // --- Kommentare (Kollaboration Phase 5) -----------------------------------
  const onCommentAt = (info: { world: { x: number; y: number }; nodeId: string | null }) => {
    if (readOnlyRef.current) return;
    setCommentDraft({
      anchor: info.nodeId
        ? { nodeId: info.nodeId }
        : { x: Math.round(info.world.x), y: Math.round(info.world.y) },
      world: info.world,
    });
  };

  const addComment = (anchor: CommentAnchor, text: string) => {
    if (readOnlyRef.current || !user) return;
    const comment: SceneComment = {
      id: newId(),
      authorId: user.id,
      authorName: user.displayName,
      anchor,
      text,
      createdAt: new Date().toISOString(),
      resolved: false,
    };
    setScene((s) => ({ ...s, comments: [...(s.comments ?? []), comment] }));
    markDirty();
  };

  const submitCommentDraft = (text: string) => {
    if (!commentDraft) return;
    addComment(commentDraft.anchor, text);
    setOpenThreadKey(commentAnchorKey(commentDraft.anchor));
    setCommentDraft(null);
    setToolState('select'); // ein Kommentar pro Werkzeug-Aktivierung, danach zurueck zur Auswahl
  };

  const setThreadResolved = (key: string, resolved: boolean) => {
    if (readOnlyRef.current) return;
    setScene((s) => ({
      ...s,
      comments: (s.comments ?? []).map((c) => (commentAnchorKey(c.anchor) === key ? { ...c, resolved } : c)),
    }));
    markDirty();
    if (resolved) setOpenThreadKey(null);
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectedPort(null);
    setPendingFrom(null);
  };

  // Klick ersetzt die Auswahl (gruppen-expandiert), Shift-Klick toggelt die Gruppe.
  const selectNode = (id: string, shiftKey: boolean) => {
    setSelectedPort(null);
    const expanded = expandSelection(scene.nodes, [id]);
    setSelectedIds((prev) => {
      if (!shiftKey) return expanded;
      const allSelected = expanded.every((x) => prev.includes(x));
      if (allSelected) return prev.filter((x) => !expanded.includes(x));
      return [...new Set([...prev, ...expanded])];
    });
  };

  const onNodeMouseDown = (id: string, shiftKey: boolean) => {
    if (tool === 'connect') {
      handleConnectClick({ nodeId: id });
    } else if (tool === 'select') {
      selectNode(id, shiftKey);
    }
  };

  const onMarqueeSelect = (ids: string[]) => {
    setSelectedIds(ids.length === 0 ? [] : expandSelection(scene.nodes, ids));
  };

  // Rechtsklick auf Canvas/Knoten: Ziel ggf. selektieren und Kontextmenü an der Position öffnen.
  const onCanvasContextMenu = (info: { clientX: number; clientY: number; nodeId: string | null }) => {
    if (info.nodeId && !selectedIds.includes(info.nodeId)) selectNode(info.nodeId, false);
    const rect = containerRef.current?.getBoundingClientRect();
    setCtxMenu({
      x: info.clientX - (rect?.left ?? 0),
      y: info.clientY - (rect?.top ?? 0),
      nodeId: info.nodeId,
    });
  };

  // Klick auf einen Switch-Port: im Connect-Werkzeug einstecken, im Auswahl-Werkzeug Port waehlen.
  const onPortMouseDown = (nodeId: string, portIndex: number) => {
    if (tool === 'connect') {
      handleConnectClick({ nodeId, portIndex });
    } else if (tool === 'select') {
      setSelectedIds([]);
      setSelectedPort({ nodeId, portIndex });
    }
  };

  const save = useCallback(async () => {
    if (collabEnabled || readOnly) return;
    setStatus('saving');
    setError(null);
    try {
      await api.saveDrawingContent(drawingId, scene);
      dirtyRef.current = false;
      setStatus('saved');
      setLastSavedAt(new Date());
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setLockState('lost');
      setStatus('error');
      if (err instanceof ApiError && err.status === 413) {
        setError(t('editor.status.errorTooLarge'));
      } else {
        setError(err instanceof ApiError ? err.message : t('errors.generic'));
      }
    }
  }, [drawingId, scene, t, readOnly, collabEnabled]);

  // Autosave: nach Änderungen mit kurzer Verzögerung speichern.
  useEffect(() => {
    if (collabEnabled || readOnly || !dirtyRef.current) return;
    const timer = setTimeout(() => void save(), 1500);
    return () => clearTimeout(timer);
  }, [scene, save, readOnly, collabEnabled]);

  // Flush-Save: ungespeicherte Änderungen beim Verlassen sofort sichern.
  // keepalive lässt den Request das Entladen der Seite überleben (~64-KB-Limit,
  // größere Szenen deckt der Debounce-Autosave ab). Nie bei readOnly/Lock-Verlust.
  const sceneRef = useRef(scene);
  sceneRef.current = scene;
  const flushSave = useCallback(async () => {
    if (collabEnabled || readOnlyRef.current || !dirtyRef.current) return;
    dirtyRef.current = false;
    try {
      await api.saveDrawingContent(drawingId, sceneRef.current, { keepalive: true });
      setStatus('saved');
      setLastSavedAt(new Date());
    } catch {
      dirtyRef.current = true;
    }
  }, [drawingId, collabEnabled]);

  useEffect(() => {
    const onPageHide = () => void flushSave();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') void flushSave();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [flushSave]);

  // Sicherheitsnetz: Warnen, wenn die Seite mit ungespeicherten Änderungen verlassen wird.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!readOnlyRef.current && dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Sperre: beim Öffnen automatisch holen, per Heartbeat halten, beim Verlassen freigeben.
  // Ist die Zeichnung blockiert, wird alle 15 s erneut versucht (kein Neuladen noetig).
  const LOCK_RETRY_MS = 15_000;
  useEffect(() => {
    if (!editable || collabEnabled) return;
    let active = true;
    let interval: ReturnType<typeof setInterval> | undefined;

    const startHeartbeat = () => {
      interval = setInterval(() => {
        api.heartbeatLock(drawingId).catch(() => {
          setLockState('lost');
          if (interval) clearInterval(interval);
        });
      }, LOCK_HEARTBEAT_SECONDS * 1000);
    };

    const tryAcquire = async (): Promise<boolean> => {
      try {
        await api.acquireLock(drawingId);
        if (!active) return true;
        setLockState('locked');
        startHeartbeat();
        return true;
      } catch {
        if (!active) return true;
        setLockState('blocked');
        api
          .getLock(drawingId)
          .then((r) => r.lock && setLockHolder(r.lock.lockedByName))
          .catch(() => undefined);
        return false;
      }
    };

    void (async () => {
      if (await tryAcquire()) return;
      // Blockiert: regelmaessig erneut versuchen, bis der andere Benutzer fertig ist.
      interval = setInterval(() => {
        void (async () => {
          if (!active) return;
          try {
            await api.acquireLock(drawingId);
            if (!active) return;
            if (interval) clearInterval(interval);
            setLockState('locked');
            startHeartbeat();
          } catch {
            /* weiter blockiert */
          }
        })();
      }, LOCK_RETRY_MS);
    })();

    return () => {
      active = false;
      if (interval) clearInterval(interval);
      // Beim Unmount erst offene Änderungen sichern, dann die Sperre freigeben.
      void flushSave().finally(() => {
        api.releaseLock(drawingId).catch(() => undefined);
      });
    };
  }, [drawingId, editable, flushSave, collabEnabled]);

  // Tastenkürzel: Listener einmal registrieren, aktuelle Aktionen über Ref lesen.
  const shortcutRef = useRef({
    doUndo,
    doRedo,
    save,
    duplicateSelected,
    removeSelected,
    reorderSelection,
    groupSelection,
    ungroupSelection,
    clearSelection,
    zoomBy,
    fitToSelection,
    hasSelection: selectedIds.length > 0,
    readOnly,
  });
  shortcutRef.current = {
    doUndo,
    doRedo,
    save,
    duplicateSelected,
    removeSelected,
    reorderSelection,
    groupSelection,
    ungroupSelection,
    clearSelection,
    zoomBy,
    fitToSelection,
    hasSelection: selectedIds.length > 0,
    readOnly,
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      const a = shortcutRef.current;
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (e.key === ' ') {
        // Leertaste halten = temporäres Pan-Werkzeug.
        if (!e.repeat) setSpaceDown(true);
        e.preventDefault();
      } else if (mod && key === 'z' && !e.shiftKey) {
        e.preventDefault();
        a.doUndo();
      } else if (mod && (key === 'y' || (key === 'z' && e.shiftKey))) {
        e.preventDefault();
        a.doRedo();
      } else if (mod && key === 's') {
        e.preventDefault();
        void a.save();
      } else if (mod && key === 'd') {
        e.preventDefault();
        a.duplicateSelected();
      } else if (mod && key === 'g') {
        e.preventDefault();
        if (e.shiftKey) a.ungroupSelection();
        else a.groupSelection();
      } else if (mod && (e.key === '[' || e.key === '{')) {
        e.preventDefault();
        a.reorderSelection(e.shiftKey ? 'back' : 'backward');
      } else if (mod && (e.key === ']' || e.key === '}')) {
        e.preventDefault();
        a.reorderSelection(e.shiftKey ? 'front' : 'forward');
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && a.hasSelection && !a.readOnly) {
        e.preventDefault();
        a.removeSelected();
      } else if (e.key === 'Escape') {
        a.clearSelection();
      } else if (e.key === '?') {
        e.preventDefault();
        setHelpOpen(true);
      } else if (!mod && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        a.zoomBy(1.2);
      } else if (!mod && e.key === '-') {
        e.preventDefault();
        a.zoomBy(1 / 1.2);
      } else if (!mod && key === 'f') {
        e.preventDefault();
        a.fitToSelection();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ') setSpaceDown(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const selectedNodes = useMemo(
    () => scene.nodes.filter((n) => selectedIds.includes(n.id)),
    [scene.nodes, selectedIds],
  );
  const single = selectedIds.length === 1;
  const selectedNode = (single ? selectedNodes[0] : undefined) ?? null;
  const selectedConnector = (single ? scene.connectors.find((c) => c.id === selectedIds[0]) : undefined) ?? null;
  const portSelection = useMemo(() => {
    if (!selectedPort) return null;
    const node = scene.nodes.find((n) => n.id === selectedPort.nodeId);
    if (!node?.switch) return null;
    return { node, portIndex: selectedPort.portIndex };
  }, [selectedPort, scene.nodes]);
  const switchNodes = useMemo(
    () => scene.nodes.filter((n) => n.type === 'switch').map((n) => ({ id: n.id, label: n.label })),
    [scene.nodes],
  );
  const firewallNodes = useMemo(
    () => scene.nodes.filter((n) => n.type === 'firewall').map((n) => ({ id: n.id, label: n.label })),
    [scene.nodes],
  );

  return (
    <div
      ref={editorRootRef}
      data-testid="editor-root"
      className="flex h-[calc(100dvh-8rem)] min-h-[30rem] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
    >
      <Toolbar
        tool={tool}
        status={collabEnabled ? 'live' : status}
        savedAt={lastSavedAt ? formatInZone(lastSavedAt, undefined, 'HH:mm') : null}
        hasSelection={selectedIds.length > 0}
        readOnly={readOnly}
        canUndo={canUndo}
        canRedo={canRedo}
        zoomPercent={Math.round(scale * 100)}
        isFullscreen={isFullscreen}
        lineBridges={scene.lineBridges !== false}
        onSetTool={setTool}
        onDelete={removeSelected}
        onDuplicate={duplicateSelected}
        onUndo={doUndo}
        onRedo={doRedo}
        onSave={() => void save()}
        onZoomIn={() => zoomBy(1.2)}
        onZoomOut={() => zoomBy(1 / 1.2)}
        onZoomReset={() => {
          fitBox({ x: canvas.x, y: canvas.y, width: canvas.width, height: canvas.height });
        }}
        onZoomFit={fitToView}
        onToggleFullscreen={toggleFullscreen}
        onToggleLineBridges={toggleLineBridges}
        onExport={handleExport}
        onOpenHelp={() => setHelpOpen(true)}
      />
      {readOnly && (
        <div className="bg-amber-50 px-3 py-1 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          {!editable
            ? t('editor.readOnlyView')
            : collabEnabled
              ? connection === 'denied'
                ? t('editor.collabSessionLost')
                : t('editor.collabConnecting')
              : lockState === 'blocked'
                ? t('editor.lockedBy', { name: lockHolder })
                : lockState === 'lost'
                  ? t('editor.lockLost')
                  : t('editor.acquiringLock')}
        </div>
      )}
      {!readOnly && collabEnabled && connection === 'disconnected' && (
        <div className="bg-amber-50 px-3 py-1 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          {t('editor.collabReconnecting')}
        </div>
      )}
      {error && (
        <div className="p-2">
          <Alert kind="error">{error}</Alert>
        </div>
      )}
      {!readOnly && tool === 'connect' && (
        <div className="bg-blue-50 px-3 py-1 text-xs text-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
          {pendingFrom ? t('editor.connectHintTarget') : t('editor.connectHintSource')}
        </div>
      )}
      <div className="relative flex min-h-0 flex-1">
        <InsertSidebar
          readOnly={readOnly}
          onAdd={addNode}
          onOpenLibrary={() => setLibraryOpen((v) => !v)}
          onOpenVlans={() => setVlansOpen(true)}
          onOpenImport={() => setImportOpen(true)}
          onOpenUserDirectory={() => setUserDirOpen(true)}
          onAddDevice={addDevice}
          onAddSwitch={addSwitch}
          onAddHost={addHost}
          onAddClusterStorage={addClusterStorage}
          onAddSwitchLegend={addSwitchLegend}
          onAddFirewall={addFirewall}
          onAddFirewallLegend={addFirewallLegend}
        />
        {/* Bild-Bibliothek: inline ab md, mobil als Overlay-Drawer von links */}
        {(libraryOpen || imagePickNodeId !== null) && (
          <>
            <div
              className="fixed inset-0 z-40 bg-slate-900/40 md:hidden"
              onClick={() => {
                setLibraryOpen(false);
                setImagePickNodeId(null);
              }}
            />
            <div className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] md:static md:z-auto md:w-64 md:max-w-none md:shrink-0">
              <ImageLibraryPanel
                onClose={() => {
                  setLibraryOpen(false);
                  setImagePickNodeId(null);
                }}
                onInsert={(image) => {
                  if (imagePickNodeId !== null) pickDeviceImage(image);
                  else addImageNode(image);
                }}
              />
            </div>
          </>
        )}
        <div ref={containerRef} className="relative min-h-0 flex-1 overflow-hidden bg-slate-200 dark:bg-slate-950">
          <EditorCanvas
            scene={scene}
            selectedIds={selectedIds}
            pendingFrom={pendingFrom}
            onContextMenu={onCanvasContextMenu}
            draggable={tool === 'select' && !readOnly}
            panViaSpace={spaceDown}
            connectMode={tool === 'connect'}
            commentMode={tool === 'comment'}
            onCommentAt={onCommentAt}
            viewportWidth={viewport.width}
            viewportHeight={viewport.height}
            scale={scale}
            stagePos={stagePos}
            canvas={canvas}
            exporting={exporting}
            stageRef={stageRef}
            onNodeMouseDown={onNodeMouseDown}
            onPortMouseDown={onPortMouseDown}
            selectedPort={selectedPort}
            onConnectorMouseDown={(id) => tool === 'select' && setSelectedIds([id])}
            onEmptyMouseDown={clearSelection}
            onMarqueeSelect={onMarqueeSelect}
            onNodesChange={updateNodes}
            onConnectorChange={updateConnector}
            onViewportChange={onViewportChange}
            onPointerWorldMove={collabEnabled ? presence.setCursor : undefined}
            remoteCursors={collabEnabled ? presence.others : undefined}
            remoteSelections={remoteSelections}
          />
          {/* Praesenzliste: wer ist gerade in dieser Zeichnung (nur im Collab-Modus). */}
          {collabEnabled && (
            <PresenceBar
              self={user ? { userId: user.id, name: user.displayName, color: presenceColor(user.id) } : null}
              others={presence.others}
              followingUserId={followingUserId}
              onFollowToggle={(userId) => setFollowingUserId((cur) => (cur === userId ? null : userId))}
            />
          )}
          {/* Kommentar-Pins + Threads (Phase 5); DOM-Overlay, nie im Export. */}
          <CommentsOverlay
            scene={scene}
            scale={scale}
            stagePos={stagePos}
            viewport={viewport}
            readOnly={readOnly}
            openThreadKey={openThreadKey}
            onOpenThread={setOpenThreadKey}
            draft={commentDraft}
            onCancelDraft={() => setCommentDraft(null)}
            onSubmitDraft={submitCommentDraft}
            onReply={addComment}
            onSetThreadResolved={setThreadResolved}
          />
          {/* Mobiler Umschalter für das Eigenschaften-Panel */}
          <button
            type="button"
            onClick={() => setPropsOpenMobile(true)}
            className="absolute bottom-3 left-3 z-10 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white/90 px-3 py-2 text-xs font-medium shadow-sm backdrop-blur focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 md:hidden dark:border-slate-600 dark:bg-slate-800/90"
          >
            ⚙ {t('editor.properties')}
          </button>
          {/* Minimap ein-/ausklappbar */}
          <div className="absolute bottom-3 right-3 flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={() => setMinimapOpen((v) => !v)}
              aria-label={t('editor.toggleMinimap')}
              aria-expanded={minimapOpen}
              className="rounded-md border border-slate-300 bg-white/90 px-2 py-1 text-xs shadow-sm backdrop-blur hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:border-slate-600 dark:bg-slate-800/90"
            >
              {minimapOpen ? '▾' : '▴'} {t('editor.minimap')}
            </button>
            {minimapOpen && (
              <Minimap
                scene={scene}
                canvas={canvas}
                viewportWidth={viewport.width}
                viewportHeight={viewport.height}
                scale={scale}
                stagePos={stagePos}
                onJump={(pos) => {
                  setFollowingUserId(null); // eigene Interaktion (Minimap-Sprung) beendet das Folgen
                  setStagePos(pos);
                }}
              />
            )}
          </div>
          {/* Rechtsklick-Kontextmenü */}
          {ctxMenu && (
            <>
              <div
                className="absolute inset-0 z-20"
                onClick={() => setCtxMenu(null)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setCtxMenu(null);
                }}
              />
              <div
                role="menu"
                className="absolute z-30 min-w-[12rem] rounded-md border border-slate-200 bg-white p-1 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-800"
                style={{
                  left: Math.max(4, Math.min(ctxMenu.x, viewport.width - 200)),
                  top: Math.max(4, Math.min(ctxMenu.y, viewport.height - 240)),
                }}
              >
                {ctxMenu.nodeId && (
                  <>
                    <MenuItem
                      disabled={readOnly}
                      onClick={() => {
                        duplicateSelected();
                        setCtxMenu(null);
                      }}
                    >
                      {t('editor.duplicate')}
                    </MenuItem>
                    <MenuItem
                      disabled={readOnly}
                      onClick={() => {
                        const id = ctxMenu.nodeId;
                        setCtxMenu(null);
                        if (!id) return;
                        setTool('connect');
                        setPendingFrom({ nodeId: id });
                      }}
                    >
                      {t('editor.connectFromHere')}
                    </MenuItem>
                    <MenuItem
                      disabled={readOnly}
                      onClick={() => {
                        reorderSelection('front');
                        setCtxMenu(null);
                      }}
                    >
                      {t('editor.layerFront')}
                    </MenuItem>
                    <MenuItem
                      disabled={readOnly}
                      onClick={() => {
                        reorderSelection('back');
                        setCtxMenu(null);
                      }}
                    >
                      {t('editor.layerBack')}
                    </MenuItem>
                    <MenuItem
                      danger
                      disabled={readOnly}
                      onClick={() => {
                        removeSelected();
                        setCtxMenu(null);
                      }}
                    >
                      {t('editor.delete')}
                    </MenuItem>
                  </>
                )}
                <MenuItem
                  onClick={() => {
                    fitToSelection();
                    setCtxMenu(null);
                  }}
                >
                  {t('editor.zoomToSelection')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    fitToView();
                    setCtxMenu(null);
                  }}
                >
                  {t('editor.zoomFitAll')}
                </MenuItem>
              </div>
            </>
          )}
        </div>
        {/* Eigenschaften-Panel: resizable inline ab md, mobil als Overlay-Drawer von rechts */}
        {propsOpenMobile && (
          <div className="fixed inset-0 z-40 bg-slate-900/40 md:hidden" onClick={() => setPropsOpenMobile(false)} />
        )}
        <div
          ref={panelRef}
          style={{ '--pw': `${panelWidth}px` } as CSSProperties}
          className={cn(
            'md:relative md:z-auto md:flex md:w-[var(--pw)] md:max-w-none md:shrink-0',
            propsOpenMobile ? 'fixed inset-y-0 right-0 z-50 flex w-80 max-w-[88vw]' : 'hidden md:flex',
          )}
        >
          {/* Drag-Divider zum Verbreitern/Verschmälern (nur ab md) */}
          <div
            onPointerDown={startResize}
            aria-hidden="true"
            className="absolute left-0 top-0 z-10 hidden h-full w-1.5 -translate-x-1/2 cursor-col-resize bg-transparent transition-colors hover:bg-slate-300/70 md:block dark:hover:bg-slate-600/70"
          />
          {/* Schließen-Knopf nur im mobilen Drawer */}
          <button
            type="button"
            onClick={() => setPropsOpenMobile(false)}
            aria-label={t('common.close')}
            className="absolute right-2 top-2 z-10 rounded p-1 text-lg leading-none text-slate-500 hover:text-slate-800 md:hidden dark:text-slate-300 dark:hover:text-slate-100"
          >
            ×
          </button>
          <PropertyPanel
            node={selectedNode}
            nodes={scene.nodes}
            vlans={scene.vlans ?? []}
            users={scene.users ?? []}
            switchNodes={switchNodes}
            firewallNodes={firewallNodes}
            connector={selectedConnector}
            selectionCount={selectedIds.length}
            canGroup={selectedNodes.length >= 2}
            canUngroup={selectedNodes.some((n) => n.groupId !== undefined)}
            canvasFormat={(scene.canvas?.format ?? 'a4-l') as CanvasFormat}
            onChangeCanvasFormat={setCanvasFormat}
            onChangeNode={(patch) => selectedNode && updateNode(selectedNode.id, patch)}
            onChangeSelection={updateSelectedNodes}
            portSelection={portSelection}
            onChangePort={(patch) => selectedPort && updatePort(selectedPort.nodeId, selectedPort.portIndex, patch)}
            onPickDeviceImage={() => selectedNode && setImagePickNodeId(selectedNode.id)}
            onChangeConnector={(patch) => selectedConnector && updateConnector(selectedConnector.id, patch)}
            onReorder={reorderSelection}
            onGroup={groupSelection}
            onUngroup={ungroupSelection}
            onDelete={removeSelected}
          />
        </div>
      </div>

      {vlansOpen && (
        <VlanManager
          vlans={scene.vlans ?? []}
          onAdd={addVlan}
          onUpdate={updateVlan}
          onRemove={removeVlan}
          onClose={() => setVlansOpen(false)}
        />
      )}

      {importOpen && (
        <ImportDialog onPreview={importPreview} onApply={importApply} onClose={() => setImportOpen(false)} />
      )}

      {userDirOpen && (
        <UserDirectoryManager
          users={scene.users ?? []}
          onRemove={removeUser}
          onClear={clearUsers}
          onOpenImport={() => {
            setUserDirOpen(false);
            setImportOpen(true);
          }}
          onClose={() => setUserDirOpen(false)}
        />
      )}

      <ShortcutHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
